import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { motion } from 'framer-motion';
import { MapPin, Camera, Waves, Mountain, Coffee, ShoppingBag } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icon for Villa Dinoos
const villaIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="18" fill="#6b4f2d" stroke="#f5efe4" stroke-width="4"/>
      <path d="M12 18L20 12L28 18V28C28 28.5523 27.5523 29 27 29H13C12.4477 29 12 28.5523 12 28V18Z" fill="#f5efe4"/>
      <path d="M18 29V22H22V29" fill="#6b4f2d"/>
    </svg>
  `),
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

// Custom icons for different attraction types
const createCustomIcon = (color: string, iconType: string) => {
  const iconSvg = {
    beach: `<path d="M3 17h18v2H3v-2zm16.5-1.5L17 13l-1.5 2.5-2-1.5L12 16l-1.5-2.5L8 15l-1.5-2.5L5 14.5 3.5 12 2 14.5.5 12 2 9.5 3.5 12 5 9.5 6.5 12 8 9.5 9.5 12 11 9.5 12.5 12 14 9.5 15.5 12 17 9.5 18.5 12 20 9.5 21.5 12z"/>`,
    temple: `<path d="M12 2L8 6H4v14h16V6h-4l-4-4zm0 2.83L13.17 6h-2.34L12 4.83zM6 8h12v10H6V8z"/>`,
    restaurant: `<path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>`,
    attraction: `<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>`
  };

  return new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="${color}" stroke="#ffffff" stroke-width="2"/>
        ${iconSvg[iconType as keyof typeof iconSvg] || iconSvg.attraction}
      </svg>
    `),
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

interface Attraction {
  id: number;
  name: string;
  position: [number, number];
  type: 'beach' | 'temple' | 'restaurant' | 'attraction';
  description: string;
  distance: string;
  color: string;
}

const attractions: Attraction[] = [
  {
    id: 1,
    name: 'Weligama Beach',
    position: [5.9750, 80.4290],
    type: 'beach',
    description: 'Beautiful sandy beach ideal for surfing and relaxing in the sun',
    distance: '0.2 miles',
    color: '#4a90e2'
  },
  {
    id: 2,
    name: 'Taprobane Island',
    position: [5.9678931, 80.4209197],
    type: 'attraction',
    description: 'Iconic private island with rich history and stunning views',
    distance: '0.6 miles',
    color: '#8b9556'
  },
  {
    id: 3,
    name: 'Midigama Surf Point',
    position: [5.9650, 80.4100],
    type: 'beach',
    description: 'Popular surf break for experienced surfers',
    distance: '2.5 miles',
    color: '#4a90e2'
  },
  {
    id: 4,
    name: 'Snake Farm',
    position: [6.0139105, 80.4711507],
    type: 'attraction',
    description: 'Local conservation center showcasing Sri Lanka\'s reptiles',
    distance: '4.1 miles',
    color: '#cc8b65'
  },
  {
    id: 5,
    name: 'Mirissa Beach',
    position: [5.9435367, 80.4561531],
    type: 'beach',
    description: 'Whale watching tours and vibrant beach restaurants',
    distance: '5.2 miles',
    color: '#4a90e2'
  },
  {
    id: 6,
    name: 'Galle Fort',
    position: [6.0329, 80.2168],
    type: 'temple',
    description: 'UNESCO World Heritage Site with colonial architecture',
    distance: '15 miles',
    color: '#d4af37'
  },
  {
    id: 7,
    name: 'Kushtarajagala Statue',
    position: [5.9800, 80.4500],
    type: 'temple',
    description: 'Ancient Buddhist rock carving and meditation site',
    distance: '3.2 miles',
    color: '#d4af37'
  },
  {
    id: 8,
    name: 'Weligama Bay Marriott Resort',
    position: [5.9680, 80.4200],
    type: 'restaurant',
    description: 'Luxury dining with ocean views',
    distance: '1.8 miles',
    color: '#6b4f2d'
  }
];

// Villa Dinoos coordinates
const villaPosition: [number, number] = [5.9801755, 80.4225294];

const MapController: React.FC = () => {
  const map = useMap();
  
  useEffect(() => {
    // Set initial view to show villa and nearby attractions
    map.setView(villaPosition, 13);
  }, [map]);

  return null;
};

const InteractiveMap: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white rounded-lg shadow-lg overflow-hidden"
    >
      <div className="h-96 md:h-[500px] relative">
        <MapContainer
          center={villaPosition}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          className="z-10"
        >
          <MapController />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Villa Dinoos Marker */}
          <Marker position={villaPosition} icon={villaIcon}>
            <Popup className="custom-popup">
              <div className="p-2">
                <h3 className="font-serif font-semibold text-earth-brown text-lg mb-2">
                  Villa DINOOS
                </h3>
                <p className="text-gray-600 text-sm mb-2">
                  152, Bandaranayake Lane, Weligama
                </p>
                <p className="text-gray-700 text-sm">
                  Your luxury retreat in the heart of Weligama
                </p>
              </div>
            </Popup>
          </Marker>

          {/* Attraction Markers */}
          {attractions.map((attraction) => (
            <Marker
              key={attraction.id}
              position={attraction.position}
              icon={createCustomIcon(attraction.color, attraction.type)}
            >
              <Popup className="custom-popup">
                <div className="p-2">
                  <h4 className="font-semibold text-earth-brown mb-1">
                    {attraction.name}
                  </h4>
                  <p className="text-xs text-earth-gold font-medium mb-2">
                    {attraction.distance} from villa
                  </p>
                  <p className="text-gray-700 text-sm">
                    {attraction.description}
                  </p>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      
      {/* Map Legend */}
      <div className="p-6 bg-earth-beige border-t border-earth-brown/10">
        <h4 className="font-serif font-semibold text-earth-brown mb-4">Map Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-earth-brown rounded-full"></div>
            <span className="text-sm text-gray-700">Villa DINOOS</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
            <span className="text-sm text-gray-700">Beaches</span>
          </div>
          <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
            <span className="text-sm text-gray-700">Cultural Sites</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-olive-600 rounded-full"></div>
            <span className="text-sm text-gray-700">Attractions</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default InteractiveMap;